import { getAuthTokenFromQueryParams } from 'util/getAuthTokenFromQueryParams';
import { useCallback } from 'react';
import { VcsMessagingControllerService } from 'api';
import { TOKEN_STORAGE_KEY, WSSURL_STORAGE_KEY } from 'consts';
import { authStore } from 'state/store';
import { selectAuthToken, selectIsEmbeddedMode, selectRequiresWaitingRoomRejoin } from 'state/selectors';
import { hasMeetMessengerBootstrapInSession } from 'features/PreJoin/utils/bootstrap/messengerMeet';
import {
    type JoinPollResult,
    resolveJoinByLinkPoll,
    resolveJoinCallPoll,
} from 'features/PreJoin/utils/waitingRoom/joinApi';
import { isJoinRejectedError } from 'features/PreJoin/utils/waitingRoom/joinErrors';
import { mustJoinThroughWaitingRoomApi } from 'features/PreJoin/utils/waitingRoom/joinPlan';
import {
    clearLivekitTokens,
    getCallIdAndWorkspaceId,
    isInRoom,
    storeLivekitTokens,
} from 'features/PreJoin/utils/waitingRoom/session';

const joinCallByLinkGuest = VcsMessagingControllerService.joinCallByLinkV2;
const joinCallByLinkOAuth = VcsMessagingControllerService.joinCallByLinkOAuthV2;
const joinCall = VcsMessagingControllerService.joinCallV2;

const hasMessengerAuthToken = (): boolean => Boolean(selectAuthToken() || getAuthTokenFromQueryParams());

type JoinByLinkApi = typeof joinCallByLinkGuest | typeof joinCallByLinkOAuth;

const getJoinCallByLinkApi = (): JoinByLinkApi => (hasMessengerAuthToken() ? joinCallByLinkOAuth : joinCallByLinkGuest);

type ConnectPayload =
    | {
          jwtToken: string;
          wssUrl: string;
          webinarMode?: import('api/types').WebinarModeData;
          moderators?: import('api/types').RoomModeratorRaw[];
      }
    | {
          waitingListId: string;
          webinarMode?: import('api/types').WebinarModeData;
          moderators?: import('api/types').RoomModeratorRaw[];
      };

const mapJoinError = (error: unknown, waitingListId?: string): unknown => {
    if (waitingListId && isJoinRejectedError(error)) {
        return 'JOIN_REJECTED';
    }
    return error;
};

const finalizeAdmittedJoin = (
    credentials: { jwtToken: string; wssUrl: string },
    webinarMode?: import('api/types').WebinarModeData,
    moderators?: import('api/types').RoomModeratorRaw[],
): { payload: ConnectPayload; error?: undefined } | { error: unknown; payload?: undefined } => {
    if (selectRequiresWaitingRoomRejoin()) {
        clearLivekitTokens();
        return { error: 'WAITING_ROOM_REAPPROVAL_REQUIRED' };
    }
    storeLivekitTokens(credentials);
    return { payload: { ...credentials, webinarMode, moderators } };
};

const finalizeJoinPollResult = (result: JoinPollResult): { payload?: ConnectPayload; error?: unknown } => {
    if (result.kind === 'admitted') {
        return finalizeAdmittedJoin(result.credentials, result.webinarMode, result.moderators);
    }
    if (result.kind === 'waiting') {
        return {
            payload: {
                waitingListId: result.waitingListId,
                webinarMode: result.webinarMode,
                moderators: result.moderators,
            },
        };
    }
    return { error: result.error };
};

export const useConnectToRoom = () => {
    const getTokenAndWssUrl = useCallback(async (roomId: string, userName: string, waitingListId?: string) => {
        const jwtToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
        const wssUrl = sessionStorage.getItem(WSSURL_STORAGE_KEY);

        // joinCallV2 — workspace-scoped, требует messenger session. Embedded meet имеет только OAuth-токен
        // от messenger — он валиден лишь для *OAuth* ручек. 401 на joinCallV2 ловит RefreshTokenMiddleware,
        // который шлёт на SSO и сбрасывает auth → waiting state рвётся.
        const isEmbedded = selectIsEmbeddedMode();

        if (!waitingListId && jwtToken && wssUrl) {
            // для embedded кеш JWT только после входа в комнату
            // на join идем через joinCallByLinkOAuthV2
            if (isEmbedded) {
                if (isInRoom()) {
                    return { payload: { jwtToken, wssUrl } };
                }
            } else if (!mustJoinThroughWaitingRoomApi(roomId) && !hasMeetMessengerBootstrapInSession()) {
                return { payload: { jwtToken, wssUrl } };
            }
        }

        const queryAuthToken = getAuthTokenFromQueryParams();
        if (queryAuthToken && !selectAuthToken()) {
            authStore.setAuthToken(queryAuthToken);
        }

        const { callId, workspaceId } = getCallIdAndWorkspaceId();
        const joinCallByLinkApi = getJoinCallByLinkApi();

        if (waitingListId) {
            const joinRequest = { roomId, userName, waitingListId };

            if (!isEmbedded && hasMessengerAuthToken() && callId && workspaceId) {
                try {
                    const { payload, error } = await joinCall(workspaceId, { callId, waitingListId });
                    const result = resolveJoinCallPoll(payload, error, waitingListId);
                    const finalized = finalizeJoinPollResult(result);

                    if (result.kind === 'admitted' || result.kind === 'waiting') {
                        return finalized;
                    }
                    // С waitingListId не падаем на joinCallByLinkOAuth — двойной join может завести
                    // вторую заявку и сменить id у веб-клиента.
                    return finalized;
                } catch (e) {
                    return { error: e };
                }
            }

            const apiResult = await joinCallByLinkApi(joinRequest);
            if (!apiResult) {
                return { error: new Error('Не удалось получить данные для подключения к комнате') };
            }

            const { error, payload } = apiResult;
            if (error) {
                return { error: mapJoinError(error, waitingListId) };
            }

            return finalizeJoinPollResult(resolveJoinByLinkPoll(payload, undefined, waitingListId));
        }

        const preferJoinByLink = mustJoinThroughWaitingRoomApi(roomId);
        // Messenger → Meet bootstrap: join только через joinCallByLink(OAuth), не joinCallV2.
        // joinCallV2 для workspace-участника может вернуть JWT без waiting room, хотя звонок с approval.
        const canUseJoinCallV2FastPath =
            !isEmbedded && callId && workspaceId && !preferJoinByLink && !hasMeetMessengerBootstrapInSession();

        if (canUseJoinCallV2FastPath) {
            try {
                const { payload, error } = await joinCall(workspaceId, { callId });
                return finalizeJoinPollResult(resolveJoinCallPoll(payload, error));
            } catch (e) {
                return { error: e };
            }
        }

        const initialRequest = { roomId, userName };
        const apiResult = await joinCallByLinkApi(initialRequest);
        if (!apiResult) {
            return { error: new Error('Не удалось получить данные для подключения к комнате') };
        }

        const { error, payload } = apiResult;
        if (error) {
            return { error: mapJoinError(error, waitingListId) };
        }

        return finalizeJoinPollResult(resolveJoinByLinkPoll(payload, undefined, waitingListId));
    }, []);

    const getCleanName = (name: string) => name.slice().replace(/\s+/gi, ' ').trim();

    const isUserNameValid = (name: string) => {
        if (!name) {
            return false;
        }
        return getCleanName(name).length > 0;
    };

    return {
        getTokenAndWssUrl,
        isUserNameValid,
        getCleanName,
    };
};
